import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-blue-300 focus-visible:ring-offset-1 disabled:pointer-events-none disabled:bg-gray-200 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-white hover:brightness-125 disabled:bg-gray-200 disabled:text-white disabled:border-none disabled:cursor-not-allowed',
        destructive: 'bg-destructive text-neutral-50 hover:bg-destructive/90',
        success: 'bg-success text-white hover:brightness-125',
        outline:
          'border border-primary bg-white hover:bg-primary text-primary hover:text-white disabled:bg-gray-200 disabled:text-white disabled:border-none disabled:cursor-not-allowed',
        outlineSuccess:
          'border border-success bg-white hover:bg-success text-primary hover:text-white disabled:bg-gray-200 disabled:text-white disabled:border-none disabled:cursor-not-allowed',
        outlineDestructive:
          'border border-destructive bg-white hover:bg-destructive text-destructive hover:text-white disabled:bg-gray-200 disabled:text-white disabled:border-none disabled:cursor-not-allowed',
        secondary: 'bg-neutral-100 text-primary hover:bg-neutral-100/80',
        ghost: 'p-1 hover:bg-neutral-100 hover:text-primary hover:rounded-sm',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-[50px] max-w-[150px] w-full',
        long: 'h-[40px] max-w-[180px] w-full',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
